import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Car, Plus, Search, X, AlertTriangle } from 'lucide-react';
import { CustomerVehicle } from '../../lib/customers';

interface SelectVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (vehicle: CustomerVehicle) => void;
  vehicles: CustomerVehicle[];
  selectedVehicles?: CustomerVehicle[];
  onAddVehicle: () => void;
}

export default function SelectVehicleModal({
  isOpen,
  onClose,
  onSelect,
  vehicles,
  selectedVehicles = [],
  onAddVehicle,
}: SelectVehicleModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [localVehicles, setLocalVehicles] = useState<CustomerVehicle[]>([]);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setLocalVehicles(vehicles);
    }
  }, [isOpen, vehicles]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setLocalVehicles([]);
      setSearchQuery('');
    }
  };

  const handleAddVehicle = () => {
    onAddVehicle(); // Let parent handle both closing this modal and opening create modal
  };

  const filteredVehicles = localVehicles.filter(vehicle => {
    const searchLower = searchQuery.toLowerCase();
    return (
      `${vehicle.make} ${vehicle.model}`.toLowerCase().includes(searchLower) ||
      vehicle.color?.toLowerCase().includes(searchLower) ||
      vehicle.license_plate?.toLowerCase().includes(searchLower) ||
      vehicle.vin?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100010]`}
      onClick={onClose}
      style={{
        zIndex: 100010,
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-black border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                Select Vehicle
              </h2>
              <button 
                onClick={onClose} 
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Search and Add Vehicle Section */}
          <div className="bg-white border-b border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vehicles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                />
              </div>
              <button
                onClick={handleAddVehicle}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-2 inline" />
                Add Vehicle
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="space-y-3 mx-6 mt-6">
              {filteredVehicles.map((vehicle) => {
                const isSelected = selectedVehicles.some(v => v.id === vehicle.id);
                return (
                  <button
                    key={vehicle.id}
                    onClick={() => {
                      if (isSelected) return;
                      onSelect(vehicle);
                    }}
                    disabled={isSelected}
                    className={`w-full text-left p-4 rounded-lg transition-colors ${
                      isSelected
                        ? 'bg-orange-100 border border-orange-200 opacity-60 cursor-not-allowed'
                        : 'bg-black hover:bg-gray-900'
                    }`}
                  >
                    <div className="w-full">
                      <div className={`font-medium truncate ${
                        isSelected ? 'text-gray-900' : 'text-white'
                      }`}>
                        {vehicle.year} {vehicle.make} {vehicle.model}
                        {isSelected && (
                          <div className="flex items-center mt-1">
                            <AlertTriangle className="h-3 w-3 text-orange-500 mr-1 flex-shrink-0" />
                            <span className="text-xs text-orange-600 font-medium">Already added to this offer</span>
                          </div>
                        )}
                      </div>
                      {vehicle.color && (
                        <div className={`text-sm mt-1 ${
                          isSelected ? 'text-gray-600' : 'text-gray-300'
                        }`}>{vehicle.color}</div>
                      )}
                      {vehicle.license_plate && (
                        <div className={`text-xs mt-1 ${
                          isSelected ? 'text-gray-500' : 'text-gray-400'
                        }`}>License: {vehicle.license_plate}</div>
                      )}
                    </div>
                  </button>
                );
              })}

            </div>
            
            {vehicles.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Car className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles</h3>
                <p className="text-sm text-gray-600 mb-6">
                  {searchQuery ? 'Try adjusting your search terms' : 'Add a vehicle to get started'}
                </p>
                <button
                  onClick={handleAddVehicle}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vehicle
                </button>
              </div>
            ) : filteredVehicles.length === 0 && searchQuery && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Car className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching vehicles</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Try adjusting your search terms
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}